import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {CUSTOM_DIMENSIONS_BOX_DISTANCE_FROM_CURSOR, CUSTOM_DIMENSIONS_BOX_ID} from './custom-scaled-dimensions-info-box.types';
import {useAppSelector} from '@/hooks';
import {Text, TextSize} from '@Components/text';
import {roundPrecision} from '@Utils/math.util';
import {formatPixelLengthInPosterUnits} from '@Libraries/poster-object.library';
import styles from './custom-scaled-dimensions-info-box.module.scss';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {useSingleActivePosterItem} from '@Hooks/poster-editor/useSingleActivePosterItem';
import type {CustomItemControlsProps} from '@Components/poster-editor/components/custom-item-controls/components/custom-item-controls-wrapper/custom-item-controls-wrapper.types';
import {isMobile, isTablet, isWindows} from 'react-device-detect';
import {isTouchDevice} from '@Utils/browser.util';

export function CustomScaledDimensionsInfoBox(props: CustomItemControlsProps): ReactElement | null {
  const activeItem = useSingleActivePosterItem(props.pageHashedId);
  const [cursorPos, setCursorPos] = useState({x: 0, y: 0});
  const [boxSize, setBoxSize] = useState({width: 0, height: 0});
  const arePosterItemsScaling = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsScaling[props.pageHashedId];
  });
  const arePosterItemsRotating = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsRotating[props.pageHashedId];
  });

  const isDeviceTouchScreen = isMobile || isTablet || (isTouchDevice() && !isWindows);

  useEffect(() => {
    const handlePointerDownOrMove = (event: PointerEvent): void => {
      if (cursorPos.x !== event.clientX || cursorPos.y !== event.clientY) {
        setCursorPos({
          x: event.clientX,
          y: event.clientY,
        });
      }
    };

    document.addEventListener('pointermove', handlePointerDownOrMove);
    document.addEventListener('pointerdown', handlePointerDownOrMove);
    return (): void => {
      document.removeEventListener('pointermove', handlePointerDownOrMove);
      document.removeEventListener('pointerdown', handlePointerDownOrMove);
    };
  }, []);

  useEffect(() => {
    const box = document.getElementById('custom-info-box');
    if (box) {
      setBoxSize({width: box.offsetWidth, height: box.offsetHeight});
    }
  }, [cursorPos]);

  const getOffsetFromPointer = (): number => {
    if (isDeviceTouchScreen) {
      return -CUSTOM_DIMENSIONS_BOX_DISTANCE_FROM_CURSOR * 5;
    }

    return CUSTOM_DIMENSIONS_BOX_DISTANCE_FROM_CURSOR;
  };

  const getDimensionsboxPosition = (): {x: number; y: number} => {
    return adjustPositionToFitWithinViewport(cursorPos.x + getOffsetFromPointer(), cursorPos.y + getOffsetFromPointer());
  };

  const adjustPositionToFitWithinViewport = (x: number, y: number): {x: number; y: number} => {
    return {
      x: Math.max(0, Math.min(x, window.innerWidth - boxSize.width - CUSTOM_DIMENSIONS_BOX_DISTANCE_FROM_CURSOR)),
      y: Math.max(0, Math.min(y, window.innerHeight - boxSize.height - CUSTOM_DIMENSIONS_BOX_DISTANCE_FROM_CURSOR)),
    };
  };

  const getDimensionsBoxText = (page: Page): string => {
    let angle = roundPrecision(props.positionProperties.angle, 0);
    if (angle < 0) {
      angle = angle + 360;
    }
    if (arePosterItemsRotating) {
      return `${angle}°`;
    }

    const width = formatPixelLengthInPosterUnits(page.poster, props.positionProperties.scaledWidth - props.positionProperties.strokeWidth);
    const height = formatPixelLengthInPosterUnits(page.poster, props.positionProperties.scaledHeight - props.positionProperties.strokeWidth);
    return activeItem?.isLine() ? `L ${width}, ${angle}°` : `w ${width}  ·  h ${height}`;
  };

  const getCustomScaledDimensionsInfoBoxComponent = (): ReactElement | null => {
    if (window.posterEditor?.whiteboard?.mode.isWebpage() || window.posterEditor?.whiteboard?.mode.isGeneration()) {
      return null;
    }

    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return null;
    }

    const activeSelection = page.activeSelection.getActiveObject();
    if (!activeSelection) {
      return null;
    }

    const infoBox = (
      <div
        id={CUSTOM_DIMENSIONS_BOX_ID}
        className={`_position-absolute flex-center spacing-p-1 ${styles.dimensionsBox}`}
        style={{
          left: `${getDimensionsboxPosition().x}px`,
          top: `${getDimensionsboxPosition().y}px`,
        }}
      >
        <Text className={'content-body'} size={TextSize.XXXSMALL} val={getDimensionsBoxText(page)} />
      </div>
    );

    return createPortal(infoBox, document.body); // to make sure that the dimensions box is visible over the add item bar, editing side panel, and navbar too
  };

  if (!arePosterItemsScaling && !arePosterItemsRotating) {
    return null;
  }

  return getCustomScaledDimensionsInfoBoxComponent();
}
